import React, { useState, useEffect } from 'react';
import { templateAnalyticsService, TemplateAnalytics, TemplateUsageStats, TemplateUser } from '../services/templateAnalyticsService';
import { useAdminTheme } from '../contexts/AdminThemeContext';

const AdminTemplateAnalytics: React.FC = () => {
  const { isDark } = useAdminTheme();
  const [analytics, setAnalytics] = useState<TemplateAnalytics | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateUsageStats | null>(null);
  const [templateUsers, setTemplateUsers] = useState<TemplateUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [activeTab, setActiveTab] = useState<'overview' | 'templates' | 'users' | 'details'>('overview');

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsData, usersData] = await Promise.all([
        templateAnalyticsService.getTemplateAnalytics(selectedPeriod),
        templateAnalyticsService.getTemplateUsers()
      ]);
      setAnalytics(analyticsData);
      setTemplateUsers(usersData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = async (templateId: string) => {
    try {
      const templateStats = await templateAnalyticsService.getTemplateUsageStats(templateId);
      setSelectedTemplate(templateStats);
      setActiveTab('details');
    } catch (error) {
      console.error('Error loading template details:', error);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
          isDark ? 'border-white' : 'border-gray-900'
        }`}></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Failed to load analytics data
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Template Analytics</h2>
          <p className={`${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>Monitor template usage and performance</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className={`px-3 py-2 rounded-lg border text-sm ${
              isDark 
                ? 'bg-gray-800 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: '📊' },
            { id: 'templates', name: 'Templates', icon: '📧' },
            { id: 'users', name: 'Users', icon: '👥' },
            { id: 'details', name: 'Details', icon: '🔍' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? isDark
                    ? 'border-blue-500 text-blue-400'
                    : 'border-blue-500 text-blue-600'
                  : isDark
                    ? 'border-transparent text-gray-400 hover:text-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-6 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Total Templates</p>
                  <p className={`text-2xl font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{formatNumber(analytics.totalTemplates)}</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Active Templates</p>
                  <p className={`text-2xl font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{formatNumber(analytics.activeTemplates)}</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Total Uses</p>
                  <p className={`text-2xl font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{formatNumber(analytics.mostUsedTemplates.reduce((sum, t) => sum + t.totalUses, 0))}</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Unique Users</p>
                  <p className={`text-2xl font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{formatNumber(templateUsers.length)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Categories */}
          <div className={`p-6 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Top Categories</h3>
            <div className="space-y-3">
              {analytics.topCategories.map((category, index) => (
                <div key={category.category} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                    <span className={`ml-3 font-medium ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {category.category.charAt(0).toUpperCase() + category.category.slice(1)}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {formatNumber(category.usage)} uses
                    </div>
                    <div className={`text-xs ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {category.count} templates
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Most Used Templates */}
          <div className={`p-6 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Most Used Templates</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className={`border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <th className={`text-left py-3 px-4 font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>Template</th>
                    <th className={`text-left py-3 px-4 font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>Category</th>
                    <th className={`text-left py-3 px-4 font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>Uses</th>
                    <th className={`text-left py-3 px-4 font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>Users</th>
                    <th className={`text-left py-3 px-4 font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.mostUsedTemplates.map((template) => (
                    <tr 
                      key={template.templateId}
                      className={`border-b ${
                        isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'
                      } cursor-pointer`}
                      onClick={() => handleTemplateClick(template.templateId)}
                    >
                      <td className="py-3 px-4">
                        <div>
                          <div className={`font-medium ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {template.templateName}
                          </div>
                          <div className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {template.createdBy === 'admin' ? '🏪 Admin' : '👤 User'}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {template.category}
                        </span>
                      </td>
                      <td className={`py-3 px-4 font-semibold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {formatNumber(template.totalUses)}
                      </td>
                      <td className={`py-3 px-4 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {formatNumber(template.uniqueUsers)}
                      </td>
                      <td className={`py-3 px-4 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {template.lastUsed ? formatDate(template.lastUsed) : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className={`p-6 rounded-lg border ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Template Users</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className={`border-b ${
                  isDark ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <th className={`text-left py-3 px-4 font-medium ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>User</th>
                  <th className={`text-left py-3 px-4 font-medium ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>Templates Used</th>
                  <th className={`text-left py-3 px-4 font-medium ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {templateUsers.slice(0, 20).map((user) => (
                  <tr key={user.userId} className={`border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <td className="py-3 px-4">
                      <div>
                        <div className={`font-medium ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {user.email}
                        </div>
                        <div className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          ID: {user.userId}
                        </div>
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {formatNumber(user.totalTemplatesUsed)}
                    </td>
                    <td className={`py-3 px-4 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {formatDate(user.lastActive)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Tab */}
      {activeTab === 'details' && selectedTemplate && (
        <div className="space-y-6">
          <div className={`p-6 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {selectedTemplate.templateName} - Usage Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>Total Uses</div>
                <div className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{formatNumber(selectedTemplate.totalUses)}</div>
              </div>
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>Unique Users</div>
                <div className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{formatNumber(selectedTemplate.uniqueUsers)}</div>
              </div>
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>Downloads</div>
                <div className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{formatNumber(selectedTemplate.downloadCount)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className={`font-semibold mb-3 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Usage by User</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedTemplate.usageByUser.map((user, index) => (
                    <div key={index} className={`flex justify-between items-center p-2 rounded ${
                      isDark ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <span className={`text-sm ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>{user.userEmail}</span>
                      <span className={`text-sm font-medium ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{user.useCount} uses</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className={`font-semibold mb-3 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Usage Over Time</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedTemplate.usageByDate.map((date, index) => (
                    <div key={index} className={`flex justify-between items-center p-2 rounded ${
                      isDark ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <span className={`text-sm ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>{date.date}</span>
                      <span className={`text-sm font-medium ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{date.uses} uses</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTemplateAnalytics;
